import React, { useState, useEffect, useMemo } from 'react';
import {
    Box,
    Typography,
    Button,
    Divider,
    Grid,
    Card,
    CardContent,
    Container,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    IconButton,
    Tooltip,
    Snackbar,
    Alert,
    CircularProgress,
    useTheme,
    useMediaQuery,
    Stack,
    InputAdornment
} from '@mui/material';
import {
    Add as AddIcon,
    Edit as EditIcon,
    Delete as DeleteIcon,
    Close as CloseIcon,
    Save as SaveIcon,
    Search as SearchIcon,
    ArrowUpward,
    ArrowDownward,
    UnfoldMore
} from '@mui/icons-material';
import { ThemeProvider } from '@mui/material/styles';
import api from '../../../services/api';
import Paginate from '../../../components/Paginate';

const itemsPerPage = 6;

const BusinesSizePage = () => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const [businessSizes, setBusinessSizes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [openDialog, setOpenDialog] = useState(false);
    const [currentBusiness, setCurrentBusiness] = useState(null);
    const [formData, setFormData] = useState({
        name: ''
    });
    const [initialFormData, setInitialFormData] = useState({
        name: ''
    });
    const [snackbar, setSnackbar] = useState({
        open: false,
        message: '',
        severity: 'success'
    });
    const [page, setPage] = useState(1);
    const [openConfirmDialog, setOpenConfirmDialog] = useState(false);
    const [businesSizeToDeleteId, setBusinesSizeToDeleteId] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [sortConfig, setSortConfig] = useState({
        key: null,
        direction: 'ascending',
    });

    useEffect(() => {
        fetchAllBusinessSizes();
    }, []);

    const fetchAllBusinessSizes = async () => {
        try {
            setLoading(true);
            const response = await api.get('/business-size');
            setBusinessSizes(response.data);
        } catch (error) {
            showSnackbar('Erro ao carregar tipos de empreendimentos', 'error');
            console.error('Error fetching businessSizes:', error);
        } finally {
            setLoading(false);
        }
    };

    const handlePageChange = (event, newPage) => {
        setPage(newPage);
    };

    const normalizeStringForSearch = (str) => {
        if (typeof str !== 'string') return '';
        return str
            .normalize("NFD")
            .replace(/[\u0300-\u036f]/g, "")
            .toLowerCase()
            .replace(/\s+/g, '');
    };

    const visibleBusiness = useMemo(() => {
        const normalizedSearchTerm = normalizeStringForSearch(searchTerm);

        let filteredBusiness = businessSizes.filter(business =>
            normalizeStringForSearch(business.name).includes(normalizedSearchTerm)
        );

        if (sortConfig.key) {
            filteredBusiness.sort((a, b) => {
                const aValue = normalizeStringForSearch(a[sortConfig.key] || '');
                const bValue = normalizeStringForSearch(b[sortConfig.key] || '');

                if (aValue < bValue) {
                    return sortConfig.direction === 'ascending' ? -1 : 1;
                }
                if (aValue > bValue) {
                    return sortConfig.direction === 'ascending' ? 1 : -1;
                }
                return 0;
            });
        }

        const startIndex = (page - 1) * itemsPerPage;
        const endIndex = startIndex + itemsPerPage;
        return filteredBusiness.slice(startIndex, endIndex);
    }, [businessSizes, page, searchTerm, sortConfig]);

    const handleOpenDialog = (business = null) => {
        setCurrentBusiness(business);
        const newFormData = {
            name: business ? business.name : ''
        };
        setFormData(newFormData);
        setInitialFormData(newFormData);
        setOpenDialog(true);
    };

    const handleCloseDialog = () => {
        setOpenDialog(false);
        setCurrentBusiness(null);
        setFormData({ name: '' });
        setInitialFormData({ name: '' });
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const hasFormChanged = () => {
        return formData.name !== initialFormData.name;
    };

    const handleSubmit = async () => {
        try {
            if (currentBusiness) {
                await api.put(`/business-size/${currentBusiness.id}`, formData);
                showSnackbar('Tipo de empreendimento atualizado com sucesso.', 'success');
            } else {
                await api.post('/business-size', formData);
                showSnackbar('Tipo de empreendimento cadastrado com sucesso.', 'success');
            }
            fetchAllBusinessSizes();
            handleCloseDialog();
        } catch (error) {
            const message = error.response?.data?.error || 'Erro ao processar a solicitação';
            showSnackbar(message, 'error');
            console.error('Error submitting business Size:', error);
        }
    };

    const handleDelete = (id) => {
        setBusinesSizeToDeleteId(id);
        setOpenConfirmDialog(true);
    };

    const handleConfirmDelete = async () => {
        try {
            await api.delete(`/business-size/${businesSizeToDeleteId}`);
            showSnackbar('Tipo de empreendimento excluído com sucesso.', 'success');

            const filteredBusiness = businessSizes.filter(business =>
                business.name.toLowerCase().includes(searchTerm.toLowerCase())
            );
            const totalPages = Math.ceil((filteredBusiness.length - 1) / itemsPerPage);

            if (page > totalPages && totalPages > 0) {
                setPage(totalPages);
            } else if (totalPages === 0) {
                setPage(1);
            }

            fetchAllBusinessSizes();
        } catch (error) {
            showSnackbar('Erro ao excluír tipo de empreendimento', 'error');
            console.error('Error deleting business Size:', error);
        } finally {
            setOpenConfirmDialog(false);
            setBusinesSizeToDeleteId(null);
        }
    };

    const handleCloseConfirmDialog = () => {
        setOpenConfirmDialog(false);
        setBusinesSizeToDeleteId(null);
    };

    const showSnackbar = (message, severity) => {
        setSnackbar({ open: true, message, severity });
    };

    const handleCloseSnackbar = () => {
        setSnackbar(prev => ({ ...prev, open: false }));
    };

    const handleSearch = (e) => {
        setSearchTerm(e.target.value);
        setPage(1);
    };

    const requestSort = (key) => {
        let direction = 'ascending';
        if (sortConfig.key === key && sortConfig.direction === 'ascending') {
            direction = 'descending';
        }
        setSortConfig({ key, direction });
    };

    return (
        <ThemeProvider theme={theme}>
            <Container maxWidth={isMobile ? 'xs' : 'xl'} sx={{ mt: isMobile ? 1 : 0, mb: 4, px: isMobile ? 1 : 2 }}>
                <Grid container spacing={isMobile ? 1 : 3} sx={{ justifyContent: 'center', mt: 0 }}>
                    <Grid item xs={12} sm={10} md={8}>
                        <Card sx={{
                            borderRadius: isMobile ? '8px' : '12px',
                            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
                            p: isMobile ? 1 : 2,
                            mt: 0
                        }}>
                            <CardContent sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                pt: isMobile ? 0 : 0
                            }}>
                                <Box sx={{
                                    display: 'flex',
                                    flexDirection: isMobile ? 'column' : 'row',
                                    justifyContent: 'space-between',
                                    alignItems: isMobile ? 'center' : 'center',
                                    mb: isMobile ? 2 : 3,
                                    width: '100%',
                                    gap: isMobile ? 2 : 0
                                }}>
                                    <Typography variant={isMobile ? 'h6' : 'h5'} sx={{ color: '#10641c', fontWeight: '600' }}>
                                        Tipos de Empreendimentos
                                    </Typography>
                                    <Button
                                        variant="contained"
                                        startIcon={<AddIcon />}
                                        onClick={() => handleOpenDialog()}
                                        sx={{
                                            bgcolor: '#10641c',
                                            '&:hover': { bgcolor: '#0a3d0f' },
                                            fontSize: isMobile ? '0.9rem' : '0.875rem',
                                            px: isMobile ? 2 : 3
                                        }}
                                    >
                                        Novo
                                    </Button>
                                </Box>

                                <Divider sx={{ mb: isMobile ? 2 : 2, width: '100%' }} />
                                <Box sx={{ width: '100%', mb: isMobile ? 2 : 2 }}>
                                    <TextField
                                        fullWidth
                                        variant="outlined"
                                        placeholder="Buscar tipo de empreendimento..."
                                        value={searchTerm}
                                        onChange={handleSearch}
                                        sx={{
                                            height: '45px',
                                            '& .MuiOutlinedInput-root': {
                                                borderRadius: isMobile ? '6px' : '8px',
                                                fontSize: isMobile ? '1rem' : '1rem',
                                                height: 'inherit',
                                                padding: '0 14px',
                                                '& .MuiOutlinedInput-notchedOutline': {
                                                    borderColor: '#ced4da',
                                                },
                                                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                                    borderColor: '#10641c',
                                                },
                                                '& .MuiInputLabel-root.Mui-focused': {
                                                    color: '#10641c',
                                                },
                                                '& .MuiInputBase-input': {
                                                    fontSize: isMobile ? '1rem' : '1rem'
                                                }
                                            }
                                        }}
                                        InputProps={{
                                            startAdornment: (
                                                <InputAdornment position="start">
                                                    <SearchIcon fontSize={isMobile ? 'medium' : 'medium'} />
                                                </InputAdornment>
                                            ),
                                        }}
                                    />
                                </Box>

                                {loading ? (
                                    <Box sx={{ display: 'flex', justifyContent: 'center', p: isMobile ? 2 : 4 }}>
                                        <CircularProgress size={isMobile ? 30 : 40} />
                                    </Box>
                                ) : (
                                    <Box sx={{ width: '100%' }}>
                                        {isMobile ? (
                                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                                {visibleBusiness.length === 0 ? (
                                                    <Typography sx={{ fontSize: '1rem', color: '#666', textAlign: 'center' }}>
                                                        Nenhum tipo de empreendimento encontrado.
                                                    </Typography>
                                                ) : (
                                                    visibleBusiness.map((business) => (
                                                        <Card key={business.id} sx={{
                                                            borderRadius: '8px',
                                                            boxShadow: '0 2px 10px rgba(0, 0, 0, 0.1)',
                                                            p: 2
                                                        }}>
                                                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                                                <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                                                                    <Typography sx={{ fontSize: '0.9rem', fontWeight: 'bold' }}>
                                                                        Nome:
                                                                    </Typography>
                                                                    <Typography sx={{ fontSize: '0.9rem' }}>
                                                                        {business.name}
                                                                    </Typography>
                                                                </Box>
                                                                <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, mt: 1 }}>
                                                                    <Tooltip title="Editar">
                                                                        <IconButton
                                                                            onClick={() => handleOpenDialog(business)}
                                                                            sx={{ color: '#1976d2', p: 1 }}
                                                                        >
                                                                            <EditIcon fontSize="medium" />
                                                                        </IconButton>
                                                                    </Tooltip>
                                                                    <Tooltip title="Excluir">
                                                                        <IconButton
                                                                            onClick={() => handleDelete(business.id)}
                                                                            sx={{ color: '#d32f2f', p: 1 }}
                                                                        >
                                                                            <DeleteIcon fontSize="medium" />
                                                                        </IconButton>
                                                                    </Tooltip>
                                                                </Box>
                                                            </Box>
                                                        </Card>
                                                    ))
                                                )}
                                            </Box>
                                        ) : (
                                            <Box sx={{ width: '100%', overflowX: 'auto' }}>
                                                <TableContainer component={Paper} sx={{ maxWidth: '100%' }}>
                                                    <Table sx={{
                                                        minWidth: 600,
                                                        '& .MuiTableCell-head': { fontWeight: 'bold' },
                                                        '& .MuiTableCell-root': {
                                                            fontSize: '0.875rem',
                                                            p: 1
                                                        }
                                                    }} aria-label="tipo de empreendimentos table">
                                                        <TableHead sx={{ bgcolor: '#f8f8f8' }}>
                                                            <TableRow>
                                                                <TableCell
                                                                    align="center"
                                                                    sx={{ width: '80%', cursor: 'pointer' }}
                                                                    onClick={() => requestSort('name')}
                                                                >
                                                                    <Box display="flex" alignItems="center" justifyContent="center">
                                                                        Nome
                                                                        {sortConfig.key === 'name' ? (
                                                                            sortConfig.direction === 'ascending' ?
                                                                                <ArrowUpward fontSize="small" sx={{ ml: 0.5 }} /> :
                                                                                <ArrowDownward fontSize="small" sx={{ ml: 0.5 }} />
                                                                        ) : (
                                                                            <UnfoldMore fontSize="small" color="action" sx={{ ml: 0.5 }} />
                                                                        )}
                                                                    </Box>
                                                                </TableCell>
                                                                <TableCell align="center" sx={{ width: '20%' }}>
                                                                    Ações
                                                                </TableCell>
                                                            </TableRow>
                                                        </TableHead>
                                                        <TableBody>
                                                            {visibleBusiness.length === 0 ? (
                                                                <TableRow>
                                                                    <TableCell align="center" colSpan={2}>
                                                                        <Typography sx={{ fontSize: '0.875rem', color: '#666' }}>
                                                                            Nenhum tipo de empreendimento encontrado.
                                                                        </Typography>
                                                                    </TableCell>
                                                                </TableRow>
                                                            ) : (
                                                                visibleBusiness.map((business) => (
                                                                    <TableRow
                                                                        key={business.id}
                                                                        sx={{ '&:last-child td, &:last-child th': { border: 0 }, '&:hover': { bgcolor: 'rgba(0, 0, 0, 0.04)' } }}
                                                                    >
                                                                        <TableCell align="center">{business.name}</TableCell>
                                                                        <TableCell align="center">
                                                                            <Stack direction="row" spacing={1} justifyContent="center">
                                                                                <Tooltip title="Editar">
                                                                                    <IconButton
                                                                                        onClick={() => handleOpenDialog(business)}
                                                                                        sx={{ color: '#1976d2', p: 1 }}
                                                                                    >
                                                                                        <EditIcon fontSize="medium" />
                                                                                    </IconButton>
                                                                                </Tooltip>
                                                                                <Tooltip title="Excluir">
                                                                                    <IconButton
                                                                                        onClick={() => handleDelete(business.id)}
                                                                                        sx={{ color: '#d32f2f', p: 1 }}
                                                                                    >
                                                                                        <DeleteIcon fontSize="medium" />
                                                                                    </IconButton>
                                                                                </Tooltip>
                                                                            </Stack>
                                                                        </TableCell>
                                                                    </TableRow>
                                                                ))
                                                            )}
                                                        </TableBody>
                                                    </Table>
                                                </TableContainer>
                                            </Box>
                                        )}
                                    </Box>
                                )}
                            </CardContent>
                        </Card>
                    </Grid>
                </Grid>

                {businessSizes.filter(business =>
                    business.name.toLowerCase().includes(searchTerm.toLowerCase())
                ).length > itemsPerPage && (
                        <Box sx={{ mt: isMobile ? 1 : 2, display: 'flex', justifyContent: 'center' }}>
                            <Paginate
                                count={Math.ceil(
                                    businessSizes.filter(business =>
                                        business.name.toLowerCase().includes(searchTerm.toLowerCase())
                                    ).length / itemsPerPage
                                )}
                                page={page}
                                onChange={handlePageChange}
                                size={isMobile ? 'small' : 'medium'}
                            />
                        </Box>
                    )}

                {/* Dialog para adicionar/editar tipo de empreendimento */}
                <Dialog
                    open={openDialog}
                    onClose={handleCloseDialog}
                    fullWidth
                    maxWidth={isMobile ? 'xs' : 'xs'}
                    fullScreen={isMobile}
                >
                    <DialogTitle
                        sx={{
                            background: 'linear-gradient(45deg, #10641c 30%, #388e3c 90%)',
                            color: 'white',
                            textAlign: 'center'
                        }}
                    >
                        {currentBusiness ? 'Editar Tipo de Empreendimento' : 'Cadastrar Tipo de Empreendimento'}
                    </DialogTitle>
                    <DialogContent
                        sx={{
                            mt: isMobile ? 1 : 2,
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            gap: 2,
                            p: isMobile ? 2 : 3,
                            borderBottom: 'none',
                            '& .MuiDialogContent-divider': { display: 'none' }
                        }}
                    >
                        <Grid container spacing={isMobile ? 1 : 2}>
                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    label="Nome"
                                    name="name"
                                    value={formData.name}
                                    onChange={handleChange}
                                    margin="normal"
                                    variant="outlined"
                                    sx={{
                                        '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                            borderColor: '#10641c',
                                        },
                                        '& .MuiInputLabel-root.Mui-focused': {
                                            color: '#10641c',
                                        },
                                        '& .MuiInputBase-input': {
                                            fontSize: isMobile ? '1rem' : '1rem',
                                            height: '50px',
                                            padding: '0 14px',
                                            width: '300px'
                                        }
                                    }}
                                />
                            </Grid>
                        </Grid>
                    </DialogContent>
                    <DialogActions sx={{ p: isMobile ? 2 : 3, display: 'flex', justifyContent: 'center', gap: 2 }}>
                        <Button
                            onClick={handleCloseDialog}
                            sx={{
                                bgcolor: 'red',
                                color: 'white',
                                '&:hover': { bgcolor: '#cc0000' },
                                fontSize: isMobile ? '0.9rem' : '0.875rem',
                                px: isMobile ? 2 : 3
                            }}
                            startIcon={<CloseIcon />}
                        >
                            Cancelar
                        </Button>
                        <Button
                            onClick={handleSubmit}
                            variant="contained"
                            disabled={currentBusiness ? !hasFormChanged() : !formData.name.trim()}
                            sx={{
                                bgcolor: '#10641c',
                                '&:hover': { bgcolor: '#0a3d0f' },
                                fontSize: isMobile ? '0.9rem' : '0.875rem',
                                px: isMobile ? 2 : 3
                            }}
                            startIcon={<SaveIcon />}
                        >
                            {currentBusiness ? 'Atualizar' : 'Salvar'}
                        </Button>
                    </DialogActions>
                </Dialog>

                {/* Diálogo de confirmação de exclusão */}
                <Dialog
                    open={openConfirmDialog}
                    onClose={handleCloseConfirmDialog}
                    fullWidth
                    maxWidth={isMobile ? 'xs' : 'sm'}
                    fullScreen={isMobile}
                >
                    <DialogTitle sx={{ bgcolor: '#d32f2f', color: 'white', textAlign: 'center', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                        Confirmar Exclusão
                    </DialogTitle>
                    <DialogContent sx={{ mt: isMobile ? 1 : 2, textAlign: 'center' }}>
                        <Typography sx={{ fontSize: isMobile ? '1rem' : '1rem' }}>
                            Tem certeza que deseja excluir este tipo de empreendimento? <br /> Esta ação é irreversível.
                        </Typography>
                    </DialogContent>
                    <DialogActions sx={{ p: isMobile ? 2 : 3, display: 'flex', justifyContent: 'center', gap: 2 }}>
                        <Button
                            onClick={handleCloseConfirmDialog}
                            sx={{
                                bgcolor: '#10641c',
                                color: 'white',
                                '&:hover': { bgcolor: '#0a3d0f' },
                                fontSize: isMobile ? '0.9rem' : '0.875rem',
                                px: isMobile ? 2 : 3
                            }}
                            startIcon={<CloseIcon />}
                        >
                            Cancelar
                        </Button>
                        <Button
                            onClick={handleConfirmDelete}
                            variant="contained"
                            color="error"
                            sx={{
                                fontSize: isMobile ? '0.9rem' : '0.875rem',
                                px: isMobile ? 2 : 3
                            }}
                            startIcon={<DeleteIcon />}
                        >
                            Excluir
                        </Button>
                    </DialogActions>
                </Dialog>

                {/* Snackbar para feedback */}
                <Snackbar
                    open={snackbar.open}
                    autoHideDuration={6000}
                    onClose={handleCloseSnackbar}
                    anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                >
                    <Alert
                        onClose={handleCloseSnackbar}
                        severity={snackbar.severity}
                        sx={{ width: '100%', fontSize: isMobile ? '0.9rem' : '0.875rem' }}
                    >
                        {snackbar.message}
                    </Alert>
                </Snackbar>
            </Container>
        </ThemeProvider>
    );
};

export default BusinesSizePage;